import React from "react";

export const PackageSummary = ({ computed, fmtC, manualDiscounts = {} }) => {
  // Apply manual discounts from the "Servicios seleccionados" editor so the
  // summary stays in sync with the bottom MRR Total. Discount is keyed by
  // `linea_id` of the main item. For the Centralita display expansion (one
  // catalog item rendered as N ext rows), the discount still lives on the
  // original base lineaId — apply it to the first ext row so the block math
  // remains correct.
  const effectivePrice = (precio, qty, lineaId, isDiscount) => {
    const base = precio * qty;
    if (isDiscount || precio < 0) return base; // negative-price lines stay as-is
    const dto = manualDiscounts[lineaId] || 0;
    return Math.max(0, base - dto);
  };
  return (
    <div className="w-2/5">
      <div className="sticky top-0 bg-[#0F0F3D] border border-[#1E1E5F] rounded-lg p-4" data-testid="package-summary">
        <h4 className="text-[#03BEE4] font-semibold text-sm mb-3">RESUMEN DEL PRESUPUESTO</h4>
        {computed.blocks.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-6">Selecciona productos para ver el resumen</p>
        ) : (() => {
          // Per-block discounted subtotals drive the visible "Subtotal" rows
          // AND the bottom "TOTAL" box. They use the same line-total formula
          // as ServicesSection so the three displays always match.
          //
          // IMPORTANT: add-ons / associated services (e.g. IP Fija attached
          // to a Fibra) carry `asociadoALineaId` so we render them nested
          // under the parent, but they have their OWN `lineaId` and can
          // receive their OWN manual discount in the bottom editor. The
          // discount must be applied per-item using the item's `lineaId` —
          // not skipped just because the item is associated to a parent.
          const blockDiscountedSubtotal = (block) => {
            let sub = 0;
            block.items.forEach(it => {
              sub += effectivePrice(it.precio || 0, it.qty || 1, it.lineaId, it.isDiscount);
            });
            return sub;
          };
          const totalConIva = computed.blocks.reduce((s, b) => s + blockDiscountedSubtotal(b), 0);
          const sinIva = totalConIva / 1.21;
          const iva = totalConIva - sinIva;
          return (
          <div className="space-y-3">
            {computed.blocks.map(block => {
              const mainItems = block.items.filter(it => !it.asociadoALineaId);
              const bonoItems = block.items.filter(it => it.asociadoALineaId);

              // Display-only expansion for Centralita (block F): a single main
              // item with `multi > 1` is visualised as N rows — one per
              // extension — with the commercial price split across them so the
              // rows sum exactly to the original price. `block.subtotal` is NOT
              // touched (still the real emitted total). Virtual `lineaId` per
              // extension follows the same pattern Block F uses when wiring
              // per-extension bonos: base, base_u2, base_u3, …
              const displayMainItems = [];
              mainItems.forEach(item => {
                if (block.key === "F" && item.multi && item.multi > 1 && item.qty === 1 && !item.isDiscount) {
                  const n = item.multi;
                  const base = Math.round((item.precio / n) * 100) / 100;
                  const rowPrices = [];
                  for (let i = 0; i < n - 1; i++) rowPrices.push(base);
                  rowPrices.push(Math.round((item.precio - base * (n - 1)) * 100) / 100);
                  for (let i = 0; i < n; i++) {
                    displayMainItems.push({
                      ...item,
                      _displayNombre: `${item.nombre} (Ext ${i + 1})`,
                      _displayPrecio: rowPrices[i],
                      _displayLineaId: i === 0 ? item.lineaId : `${item.lineaId}_u${i + 1}`,
                    });
                  }
                } else {
                  displayMainItems.push({
                    ...item,
                    _displayNombre: item.nombre,
                    _displayPrecio: item.precio,
                    _displayLineaId: item.lineaId,
                  });
                }
              });
              // Strip the trailing "(Ext N)" from bono names when we are about
              // to nest them under an explicit "(Ext N)" parent row — avoids
              // duplication in the summary.
              const stripExtSuffix = (name) =>
                (block.key === "F" ? String(name || "").replace(/\s*\(Ext\s*\d+\)\s*$/i, "") : name);

              return (
                <div key={block.key} className="border-b border-[#1E1E5F] pb-2">
                  <p className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-1">{block.name}</p>
                  {displayMainItems.map((item, idx) => {
                    const itemBonos = bonoItems.filter(b => b.asociadoALineaId === item._displayLineaId);
                    return (
                      <React.Fragment key={idx}>
                        <div className="flex justify-between text-sm py-0.5">
                          <span className={item.isDiscount ? "text-red-400" : "text-white"}>
                            {item._displayNombre}{item.qty > 1 ? ` x${item.qty}` : ""}{item.linea ? ` (línea ${item.linea})` : ""}
                          </span>
                          <span className={`ml-2 whitespace-nowrap ${item.isDiscount ? "text-red-400" : "text-[#42E695]"}`}>
                            {item.isDiscount ? "−" : ""}{fmtC(Math.abs(item._displayPrecio * item.qty))}
                          </span>
                        </div>
                        {itemBonos.map((bono, bIdx) => (
                          <div key={bIdx} className="flex justify-between text-sm py-0.5 pl-5">
                            <span className="text-cyan-300/80 text-xs">
                              └─ {stripExtSuffix(bono.nombre)}
                              {bono.esLineaExistente && <span className="text-slate-500 ml-1">(línea existente)</span>}
                            </span>
                            <span className="text-cyan-300/80 text-xs ml-2 whitespace-nowrap">{fmtC(bono.precio * bono.qty)}</span>
                          </div>
                        ))}
                      </React.Fragment>
                    );
                  })}
                  {/* Bonos orphaned from their main item: existing customer lines
                      OR extension bonos whose parent line was collapsed (e.g.
                      Centralita multi-extensión emits one parent + N extension bonos
                      with per-extension linea_ids that won't match the parent's
                      lineaId). Label "(línea existente)" only for true existing ones. */}
                  {bonoItems.filter(b => !displayMainItems.some(m => m._displayLineaId === b.asociadoALineaId)).map((bono, bIdx) => (
                    <div key={`orphan-${bIdx}`} className="flex justify-between text-sm py-0.5 pl-5">
                      <span className="text-cyan-300/80 text-xs">
                        └─ {stripExtSuffix(bono.nombre)}
                        {bono.esLineaExistente && <span className="text-slate-500 ml-1">(línea existente)</span>}
                      </span>
                      <span className="text-cyan-300/80 text-xs ml-2 whitespace-nowrap">{fmtC(bono.precio * bono.qty)}</span>
                    </div>
                  ))}
                  {/* "Este producto incluye N líneas" helper removed: every
                      generated sub-line is already rendered explicitly above
                      (one row per unit), so the helper duplicates information
                      and gets confusing when extra individual lines coexist
                      in the same block (mobile/grouped scenarios). */}
                  <div className="flex justify-between text-sm mt-1">
                    <span className="text-slate-400">Subtotal</span>
                    <span className="text-[#42E695] font-semibold">{fmtC(blockDiscountedSubtotal(block))}</span>
                  </div>
                </div>
              );
            })}
            <div className="pt-2 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Total sin IVA:</span>
                <span className="text-white">{fmtC(sinIva)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">IVA (21%):</span>
                <span className="text-white">{fmtC(iva)}</span>
              </div>
              <div className="flex justify-between bg-[#42E695]/10 p-2 rounded-lg mt-2">
                <span className="text-white font-bold">TOTAL:</span>
                <span className="text-[#42E695] font-bold text-lg">{fmtC(totalConIva)}</span>
              </div>
            </div>
          </div>
          );
        })()}
        <div className="mt-4 pt-3 border-t border-[#1E1E5F]">
          <p className="text-[10px] text-slate-500 leading-relaxed">
            Permanencia: 12 meses · Instalación: 7 días naturales · Oferta válida: 30 días
            <br />Precios condicionados a viabilidad in-situ · Oferta personal y confidencial
            <br />Instalación FTTH incluye tiradas hasta 10m; longitudes superiores pueden conllevar sobrecostes
          </p>
        </div>
      </div>
    </div>
  );
};
